'use client';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';

import React from 'react';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

export function ProductDetailPrice({
  className,
}: {
  className?: { wrapper?: string; container?: string };
}) {
  const { t } = useTranslations();
  const settings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(state => state.product);
  const hasSpecialProductStatus = product.status === 'ended' || product.status === 'arrival';
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const variants = useVariantsStore(state => state.product.variants);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedServices = useVariantsStore(state => state.selectedServices);

  const me = useCustomerStore(state => state.me);

  const getServicesPrice = (vat = false) => {
    let price = 0;
    selectedServices.forEach(service => {
      price += vat
        ? parseFloat(service.price_with_vat.toString())
        : parseFloat(service.price.toString());
    });
    return price;
  };

  const desiredObject = variants?.length ? selectedVariant : product;
  const priceWithoutVatFirst = settings?.product_detail?.price_without_vat_first?.enabled;
  const priceDisplay = getProductPriceDisplay(desiredObject ?? product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst,
  });
  const discount = priceDisplay.showPreviousPrice ? priceDisplay.discount : 0;

  //   const servicesPrice = selectedServices.reduce(
  //     (acc, service) => acc + roundPrice(service.price_with_vat, currency),
  //     0,
  //   );
  const unit = settings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';
  const mainPriceUsesVat = !priceWithoutVatFirst;
  const mainPrice =
    priceDisplay.current == null
      ? undefined
      : priceDisplay.current + getServicesPrice(mainPriceUsesVat);
  const mocPrice =
    priceDisplay.mocPrice == null
      ? undefined
      : priceDisplay.mocPrice + getServicesPrice(mainPriceUsesVat);
  const mainPriceBeforeDiscount =
    priceDisplay.beforeDiscount == null
      ? undefined
      : priceDisplay.beforeDiscount + getServicesPrice(mainPriceUsesVat);
  const secondaryPrice =
    priceDisplay.secondary == null
      ? undefined
      : priceDisplay.secondary + getServicesPrice(priceDisplay.secondaryIncludesVat);

  const renderErrorMessage = () => {
    if (selectedValues.length === product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-medium text-destructive-foreground', '')}>
          {t('text495')}
        </span>
      );
    }
    if (product?.attributes?.length && selectedValues.length < product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-medium text-destructive-foreground', '')}>
          {t('text496')}
        </span>
      );
    }
  };

  const isInicialized = useCustomerStore(s => s.isInitialized);

  if (!isInicialized) {
    return <Spinner size={'lg'} />;
  }

  if (hasSpecialProductStatus) {
    return <ProductStatusText status={product.status} />;
  }

  if ((!variants?.length || selectedVariant) && priceDisplay.priceOnRequest) return null;

  return (
    <div
      className={cn(
        'flex flex-col items-start gap-2',
        'ea-product-detail-price',
        className?.wrapper,
      )}
    >
      {!selectedVariant && !!variants?.length ? (
        renderErrorMessage()
      ) : (
        <>
          <div
            className={cn(
              'flex flex-row items-center',
              'ea-product-detail-price__container',
              className?.container,
            )}
          >
            <div>
              {priceDisplay.showMocVocPrices && mocPrice != null ? (
                <div className="flex">
                  {`${t('text1926')} ${formatPrice(mocPrice, currency, currencySettings)}${unit}`}
                </div>
              ) : null}
              <span
                className={cn(
                  'h3',
                  'ea-product-detail-price__after-discount',
                  discount > 0 ? 'text-[#A91414]' : 'text-muted',
                )}
              >
                <strong
                  className={cn(
                    'h3',
                    'font-semibold',
                    discount > 0 ? 'text-[#B80900]' : 'text-foreground',
                  )}
                >{`${formatPrice(mainPrice, currency, currencySettings)}${unit}`}</strong>
              </span>
            </div>
            {discount > 0 && (
              <span
                className={cn(
                  'ml-2 text-lg font-medium leading-[1.2] tracking-tight text-[#636264] line-through sm:text-xl sm:leading-[1.2] md:leading-[1.2] lg:text-2xl lg:leading-[1.2]',
                  'ea-product-detail-price__with-vat',
                )}
              >
                <del>{`${formatPrice(
                  mainPriceBeforeDiscount,
                  currency,
                  currencySettings,
                )}${unit}`}</del>
              </span>
            )}
          </div>
          {settings.product_detail?.show_price_without_vat?.enabled && (
            <span
              className={cn(
                'text-sm font-normal text-[#636264] sm:text-base',
                'ea-product-detail-price__without-vat',
              )}
            >
              {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
              {formatPrice(secondaryPrice, currency, currencySettings)}{' '}
              {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')} {unit}
            </span>
          )}
        </>
      )}
    </div>
  );
}
